// src/pages/NotificationLinks.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { getNotificationLinks, deleteNotificationLink } from "../services/notificationLinks";
import { Eye, Copy, Plus, Pencil, Trash2, Link2 } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";

const fmt = (d) => { try { return format(new Date(d), "dd/MM/yyyy"); } catch { return "—"; } };

// ─── URL Cell ─────────────────────────────────────────────────────────────────

function UrlCell({ url }) {
  if (!url) return <span className="text-slate-400">—</span>;
  const preview = url.length > 64 ? `${url.slice(0, 48)}…${url.slice(-12)}` : url;
  const copy = () => { navigator.clipboard.writeText(url); toast.success("URL copiada!"); };
  return (
    <div className="inline-flex items-center gap-2 max-w-[28rem]">
      <span className="font-mono text-xs text-slate-600 dark:text-slate-300 truncate cursor-pointer hover:underline" title={url} onClick={copy}>
        {preview}
      </span>
      <button type="button" onClick={copy} title="Copiar URL"
        className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function NotificationLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const data = await getNotificationLinks();
        setLinks(Array.isArray(data) ? data : []);
      } catch { toast.error("Não foi possível carregar os links"); setLinks([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await deleteNotificationLink(id);
      toast.success("Link excluído");
      setLinks((l) => l.filter((x) => x.id !== id));
    } catch { toast.error("Falha ao excluir"); }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Link2 className="w-6 h-6" /> Link de Notificação
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gerencie seus links de notificação por plataforma.
            </p>
          </div>
          <button onClick={() => navigate("/notification-links/new")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0">
            <Plus className="w-4 h-4" /> Criar novo
          </button>
        </div>

        {/* Mobile: Cards */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-sm text-slate-500">Carregando…</div>
          ) : links.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center text-sm text-slate-500">Nenhum link cadastrado.</div>
          ) : links.map((l) => (
            <div key={l.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{l.name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.platform || "—"} · {fmt(l.created_at)}</div>
                </div>
              </div>
              <div className="mb-3"><UrlCell url={l.url} /></div>
              <div className="flex gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => navigate(`/notification-links/${l.id}/edit`)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button onClick={() => setConfirmDeleteId(l.id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs">
                  <Trash2 className="w-3.5 h-3.5" /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: Tabela */}
        <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700">
              <tr className="text-left">
                {["Data Criação","Nome","Plataforma","URL","Ações"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 ${i === 4 ? "text-center" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">Carregando…</td></tr>
              ) : links.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">Nenhum link cadastrado.</td></tr>
              ) : links.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{fmt(l.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{l.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{l.platform || "—"}</td>
                  <td className="px-4 py-3"><UrlCell url={l.url} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => navigate(`/notification-links/${l.id}/edit`)} title="Editar"
                        className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setConfirmDeleteId(l.id)} title="Excluir"
                        className="p-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Excluir link"
        message="Tem certeza que deseja excluir este link de notificação?"
        confirmLabel="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}